import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, AlertTriangle, Mail, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useAuthStore } from "@/lib/auth/store";
import { ssoCallback, getSsoUrl } from "@/lib/api/auth";
import { Button } from "@/components/ui/button";
import { LogissimoGradientBar } from "@/components/ui/logissimo-gradient-bar";
import { toast } from "sonner";

import logoRvb from "@/assets/logo-rvb.png";

const CONTACT_EMAIL = "jordan.guillonneau-prestataire@laposte.fr";
const CC_EMAIL = "aude.pinson@laposte.fr";

export const Route = createFileRoute("/_auth/callback")({
  component: CallbackPage,
});

const INACTIVE_STORAGE_KEY = "sso_account_inactive";

function CallbackPage() {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(() => {
    if (sessionStorage.getItem(INACTIVE_STORAGE_KEY)) {
      return "Compte utilisateur créé mais inactif.";
    }
    return null;
  });
  const [isInactive, setIsInactive] = useState(
    () => !!sessionStorage.getItem(INACTIVE_STORAGE_KEY)
  );
  const [isRetrying, setIsRetrying] = useState(false);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || isInactive) return;
    handled.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const errorParam = params.get("error");
      const errorDescription = params.get("error_description");

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      if (!code || !state) {
        setError("Paramètres de callback manquants");
        return;
      }

      try {
        const response = await ssoCallback(code, state);

        if (!response.success || !response.data?.user || !response.data?.tokens) {
          throw new Error(response.message || "Erreur SSO");
        }

        sessionStorage.removeItem(INACTIVE_STORAGE_KEY);
        setAuth(response.data.user, response.data.tokens);
        toast.success("Connexion réussie !");

        const redirectPath =
          response.data.user.role === "admin" ? "/" : "/simulators";
        navigate({ to: redirectPath });
      } catch (err: unknown) {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ||
          (err instanceof Error ? err.message : "Erreur d'authentification");

        setError(detail);
        if (detail.toLowerCase().includes("inactif")) {
          setIsInactive(true);
          sessionStorage.setItem(INACTIVE_STORAGE_KEY, "1");
        }
      }
    };

    handleCallback();
  }, [setAuth, isInactive, navigate]);

  const handleSsoRetry = async () => {
    setIsRetrying(true);
    try {
      const response = await getSsoUrl();
      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        navigate({ to: "/login" });
      }
    } catch {
      navigate({ to: "/login" });
    }
  };

  if (error) {
    const isSsoStateError = error.toLowerCase().includes("sso state") || error.toLowerCase().includes("expired");
    const mailSubject = encodeURIComponent("Demande d'activation de compte — Logissimo Pricemaker");
    const mailBody = encodeURIComponent(
      "Bonjour,\n\nJe souhaiterais accéder à l'application Logissimo Pricemaker.\nPourriez-vous activer mon compte ?\n\nMerci,\nCordialement"
    );
    const mailtoLink = `mailto:${CONTACT_EMAIL}?cc=${CC_EMAIL}&subject=${mailSubject}&body=${mailBody}`;

    const errorTitle = isInactive
      ? "Compte en attente d'activation"
      : isSsoStateError
        ? "Session de connexion expirée"
        : "Erreur d'authentification";

    const errorDescription = isInactive
      ? "Votre compte a bien été créé mais il doit être activé par un administrateur avant de pouvoir accéder à l'application."
      : isSsoStateError
        ? "Votre session de connexion SSO a expiré ou est invalide. Veuillez réessayer."
        : error;

    const ErrorIcon = isInactive ? ShieldAlert : AlertTriangle;
    const iconColorClass = isInactive ? "text-amber-500" : "text-red-500";
    const iconBgClass = isInactive ? "bg-amber-500/10" : "bg-red-500/10";

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-laposte-blue/5" />

        <motion.div
          className="absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #FFCC00 0%, transparent 70%)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full blur-3xl opacity-15"
          style={{ background: "radial-gradient(circle, #00B2A9 0%, transparent 70%)" }}
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-lg px-6"
        >
          <div className="flex justify-center mb-8">
            <img src={logoRvb} alt="Logissimo" className="h-10 w-auto" />
          </div>

          <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl shadow-black/5 dark:shadow-black/20">
            <div className="flex flex-col items-center text-center space-y-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                className={`w-16 h-16 rounded-full ${iconBgClass} flex items-center justify-center`}
              >
                <ErrorIcon className={`w-8 h-8 ${iconColorClass}`} />
              </motion.div>

              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  {errorTitle}
                </h1>
                <p className="text-muted-foreground leading-relaxed">
                  {errorDescription}
                </p>
              </div>

              {isInactive && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="w-full space-y-4"
                >
                  <div className="bg-muted/50 rounded-xl p-5 space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      Pour activer votre compte, envoyez un email à :
                    </p>
                    <div className="space-y-1.5 text-center">
                      <p className="text-sm text-foreground font-medium">
                        {CONTACT_EMAIL}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        CC : {CC_EMAIL}
                      </p>
                    </div>
                  </div>

                  <Button
                    asChild
                    className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                    size="lg"
                  >
                    <a href={mailtoLink}>
                      <Mail className="h-4 w-4 mr-2" />
                      Envoyer un email de demande
                    </a>
                  </Button>
                </motion.div>
              )}

              {isSsoStateError && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="w-full"
                >
                  <Button
                    onClick={handleSsoRetry}
                    disabled={isRetrying}
                    className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                    size="lg"
                  >
                    {isRetrying ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Réessayer la connexion SSO
                  </Button>
                </motion.div>
              )}

              <Link
                to="/login"
                onClick={() => sessionStorage.removeItem(INACTIVE_STORAGE_KEY)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Retour à la connexion
              </Link>
            </div>
          </div>
        </motion.div>

        <LogissimoGradientBar
          variant="animated"
          height="lg"
          delay={0.5}
          className="absolute bottom-0 left-0 right-0"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center gap-4"
      >
        <Loader2 className="w-8 h-8 text-laposte-yellow animate-spin" />
        <p className="text-muted-foreground">Authentification en cours...</p>
      </motion.div>
    </div>
  );
}
